/**
 * PIXModal.tsx
 * Modal para exibir QR Code PIX e código copia e cola
 * 
 * Fase 6 - Sistema de Pagamentos PIX
 * Data: 31/01/2026
 * Atualização: 02/02/2026 - Polling automático para detectar pagamento
 */

import { useState, useEffect, useRef } from 'react';
import { X, Copy, Check, Loader2 } from 'lucide-react';
import { FaPix } from 'react-icons/fa6';
import { supabase } from '../../lib/supabase';
import { PaymentResultModal } from './PaymentResultModal';

interface PIXModalProps {
  qrCode: string; // Código copia e cola
  qrCodeUrl?: string; // URL da imagem do QR Code (opcional)
  amount: number; // Valor em reais
  orderId: string;
  onClose: () => void;
}

export function PIXModal({ qrCode, qrCodeUrl, amount, orderId, onClose }: PIXModalProps) {
  const [copied, setCopied] = useState(false);
  const [qrCodeImage, setQrCodeImage] = useState<string | null>(null);
  const [polling] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'paid' | 'failed' | 'timeout'>('pending');
  const [showResultModal, setShowResultModal] = useState(false);
  const intervalRef = useRef<number | null>(null);
  const onCloseRef = useRef(onClose);

  // Manter referência estável de onClose para evitar re-execução do polling
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Gerar QR Code usando API gratuita se não vier da Pagar.me
  useEffect(() => {
    if (qrCodeUrl) {
      setQrCodeImage(qrCodeUrl);
      return;
    }

    // Usar API pública do Google Charts para gerar QR Code
    const qrCodeApiUrl = `https://chart.googleapis.com/chart?cht=qr&chs=300x300&chl=${encodeURIComponent(qrCode)}`;
    setQrCodeImage(qrCodeApiUrl);
  }, [qrCode, qrCodeUrl]);

  // Polling para verificar pagamento via banco de dados (com clearInterval)
  useEffect(() => {
    if (!polling) return;

    let attempts = 0;
    const maxAttempts = 300; // 10 minutos (600s) - PIX pode levar até 5min
    const intervalMs = 2000;

    const tick = async () => {
      try {
        const { data: txList, error } = await supabase
          .from('payment_transactions')
          .select('status, transaction_type')
          .eq('pagarme_order_id', orderId)
          .eq('transaction_type', 'sos_credit')
          .order('created_at', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Erro ao verificar pagamento PIX:', error);
          attempts++;
        } else if (txList && txList.length > 0) {
          const tx = txList[0];
          if (tx.status === 'paid') {
            console.log('✅ Pagamento PIX confirmado!');
            setPaymentStatus('paid');
            setShowResultModal(true);
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            // Fechar automaticamente após 3 segundos
            setTimeout(() => {
              onCloseRef.current?.();
            }, 3000);
            return;
          } else if (tx.status === 'failed') {
            console.log('❌ Pagamento PIX falhou');
            setPaymentStatus('failed');
            setShowResultModal(true);
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            return;
          }
          attempts++;
        } else {
          attempts++;
        }

        if (attempts >= maxAttempts) {
          console.log('⏱️ Timeout: Pagamento não confirmado em 10 minutos. Verifique em Perfil > Pagamento');
          setPaymentStatus('timeout');
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      } catch (err) {
        console.error('Erro no polling PIX:', err);
        attempts++;
        if (attempts >= maxAttempts) {
          setPaymentStatus('timeout');
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      }
    };

    // Iniciar interval
    intervalRef.current = window.setInterval(tick, intervalMs);
    // Execução imediata para não esperar 2s na primeira checagem
    tick();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [polling, orderId]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(qrCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Erro ao copiar:', err);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <FaPix className="w-8 h-8" />
              <h2 className="text-2xl font-bold">Pagar com PIX</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <p className="text-blue-100">
            Valor: <span className="text-2xl font-bold">R$ {amount.toFixed(2)}</span>
          </p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* QR Code */}
          <div className="flex flex-col items-center">
            {qrCodeImage ? (
              <div className="bg-white p-4 rounded-xl border-2 border-gray-200 shadow-lg">
                <img
                  src={qrCodeImage}
                  alt="QR Code PIX"
                  className="w-64 h-64"
                />
              </div>
            ) : (
              <div className="w-64 h-64 bg-gray-100 rounded-xl flex items-center justify-center">
                <Loader2 className="w-12 h-12 text-gray-400 animate-spin" />
              </div>
            )}

            <div className="mt-4 text-center">
              <p className="text-sm text-gray-600">
                Abra o aplicativo do seu banco
              </p>
              <p className="text-sm text-gray-600">
                e escaneie o QR Code
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">ou</span>
            </div>
          </div>

          {/* Código Copia e Cola */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Código PIX Copia e Cola
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={qrCode}
                readOnly
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono"
              />
              <button
                onClick={handleCopy}
                className={`px-4 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                  copied
                    ? 'bg-green-100 text-green-700'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {copied ? (
                  <>
                    <Check className="w-5 h-5" />
                    Copiado
                  </>
                ) : (
                  <>
                    <Copy className="w-5 h-5" />
                    Copiar
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Instruções */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">Como pagar:</h4>
            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
              <li>Abra o app do seu banco</li>
              <li>Entre na área PIX</li>
              <li>Escolha "Pagar com QR Code" ou "Pix Copia e Cola"</li>
              <li>Escaneie o QR Code ou cole o código</li>
              <li>Confirme o pagamento</li>
            </ol>
          </div>

          {/* Status */}
          {polling && (
            <div className="flex items-center justify-center gap-2 text-gray-600">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Aguardando pagamento...</span>
            </div>
          )}

          {/* Informações */}
          <div className="text-xs text-gray-500 space-y-1">
            <p>• O pagamento é confirmado em até 5 minutos (na maioria dos bancos)</p>
            <p>• Este QR Code expira em 60 minutos</p>
            <p>• ID do pedido: {orderId.substring(0, 16)}...</p>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-6 bg-gray-50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="w-full py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>

      {/* Modal de Resultado */}
      {showResultModal && (
        <PaymentResultModal
          type={paymentStatus === 'paid' ? 'success' : 'error_failed'}
          title={
            paymentStatus === 'paid'
              ? '🎉 Créditos SOS Adicionados!'
              : '❌ Pagamento não confirmado'
          }
          message={
            paymentStatus === 'paid'
              ? 'Seus créditos foram adicionados com sucesso! Fechando automaticamente...'
              : 'Não detectamos o pagamento ainda. Se você já pagou, pode fechar este modal e verificar seus créditos em Perfil > Pagamento. O pagamento pode levar até 5 minutos para ser confirmado.'
          }
          onClose={() => {
            setShowResultModal(false);
            if (paymentStatus === 'paid') {
              onClose();
            }
          }}
          showActions={paymentStatus !== 'paid'} // Só mostra botões se não for sucesso
        />
      )}
    </div>
  );
}
